import React, { useState } from "react";
import { FileText, ChevronDown, Copy, Check } from "lucide-react";

/**
 * Tech Pack Preview — collapsed by default into a "pack ready" pill.
 *
 * Removes panel competition in the right rail. Expand on demand.
 */
export default function TechPackPreview({ pack }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const sections = pack?.sections || [];
  const ready = sections.length > 0;
  // The Booking Context section is surfaced ABOVE the collapse so the
  // technician sees how the visit was framed at the front desk without
  // having to expand the full pack.
  const bookingSection = sections.find((s) => s.key === "booking_context");
  const otherSections = sections.filter((s) => s.key !== "booking_context");

  const copy = async (e) => {
    e.stopPropagation();
    if (!pack?.plain_text) return;
    try {
      await navigator.clipboard.writeText(pack.plain_text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* silent */
    }
  };

  return (
    <section
      className="rounded-lg border border-slate-200 bg-white"
      data-testid="tech-pack-preview"
    >
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center gap-2 hover:bg-slate-50 transition-colors"
      >
        <FileText className="w-4 h-4 text-slate-500" />
        <span className="flex-1 text-left text-[13px] font-medium text-slate-700">
          Technician Intelligence Pack
        </span>
        {ready ? (
          <span className="text-[10px] uppercase tracking-wider text-teal-700 bg-teal-50 px-1.5 py-0.5 rounded">
            Pack ready
          </span>
        ) : (
          <span className="text-[10px] text-slate-400 italic">Building…</span>
        )}
        <ChevronDown
          className={`w-4 h-4 text-slate-400 transition-transform ${expanded ? "rotate-180" : ""}`}
        />
      </button>

      {/* Booking context surfaced even when collapsed — visible at a glance */}
      {bookingSection && (
        <div
          className="px-4 pb-3 -mt-1 border-t border-slate-100"
          data-testid="techpack-booking-context"
        >
          <p className="text-[11px] uppercase tracking-[0.15em] text-slate-700 font-semibold mt-2.5 mb-1.5">
            {bookingSection.label}
          </p>
          <ul className="text-[13.5px] text-slate-800 space-y-1 leading-relaxed">
            {bookingSection.lines.map((ln, i) => (
              <li key={i}>· {ln}</li>
            ))}
          </ul>
        </div>
      )}

      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-100">
          {!ready ? (
            <p className="text-[12px] text-slate-500 italic pt-3">
              Answers will assemble here as the intake progresses.
            </p>
          ) : (
            <>
              <div className="flex items-center justify-between pt-3 pb-2">
                <p className="text-[11px] text-slate-500 font-mono">{pack.headline}</p>
                <button
                  type="button"
                  onClick={copy}
                  className="text-[11px] text-slate-600 hover:text-slate-900 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-100"
                  data-testid="techpack-copy-btn"
                >
                  {copied ? (
                    <>
                      <Check className="w-3 h-3" /> Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-3 h-3" /> Copy
                    </>
                  )}
                </button>
              </div>
              <div className="space-y-2.5">
                {otherSections.map((s) => (
                  <div key={s.key} className="border-l-2 border-slate-200 pl-3">
                    <p className="text-[10px] uppercase tracking-[0.15em] text-slate-500">
                      {s.label}
                    </p>
                    <ul className="text-[12.5px] text-slate-700 mt-0.5 space-y-0.5 leading-snug">
                      {s.lines.map((ln, i) => (
                        <li key={i}>· {ln}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}
